import './styles.scoped.css'
import {Typography} from '@mui/material'
import {thousantSeparator, ignoreTimezone} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {useMobile} from '../../../../hooks'
import {SkeletonContainer, TextSkeleton} from '../../../../components'

// Rows of [label, value width] pairs, grouped the way each layout puts them on one line.
const desktopRows = [
    [['Unit Type:', 64]],
    [
        ['Start Date:', 76],
        ['End Date:', 76],
    ],
    [
        ['Total Units :', 40],
        ['Used:', 72],
        ['Remaining:', 72],
    ],
    [
        ['Total Service Rate:', 56],
        ['Rate per Unit:', 28],
    ],
    [
        ['Payor:', 200],
        ['MA/MCO Number', 70],
    ],
]
const mobileRows = [
    [['Unit Type:', 64]],
    [
        ['Start Date:', 76],
        ['End Date:', 76],
    ],
    [['Total Units :', 40]],
    [
        ['Used:', 72],
        ['Remaining:', 72],
    ],
    [['Total Service Rate:', 56]],
    [['Rate per Unit:', 28]],
    [['Payor:', 200]],
    [['MA/MCO Number', 70]],
]

const AgreementOverviewSkeleton = ({mobile}) => (
    <SkeletonContainer>
        {(mobile ? mobileRows : desktopRows).map((row, index) => (
            <div className="content" key={index}>
                {row.map(([label, width], column) => (
                    <div key={label} style={{display: 'contents'}}>
                        {column > 0 && (
                            <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                |
                            </Typography>
                        )}
                        <Typography variant={mobile ? 'h9' : 'h8'} color="text.primary">
                            {label}
                        </Typography>
                        <TextSkeleton width={width} />
                    </div>
                ))}
            </div>
        ))}
        <br />
        <Typography variant="h8" color="text.primary">
            Service Activities:
        </Typography>
        <br />
        {[
            [120, '70%'],
            [96, '45%'],
        ].map(([title, instructions], index) => (
            <div key={index}>
                <br />
                <div className="activity-card">
                    <TextSkeleton width={title} fontSize={18} />
                    <TextSkeleton width={instructions} />
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default ({serviceAgreement, serviceItem, skeleton}) => {
    const {mobile} = useMobile()

    if (skeleton) {
        return <AgreementOverviewSkeleton mobile={mobile} />
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Unit Type:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {serviceItem?.billing_unit?.replace('_', ' ')}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Start Date:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {format(new Date(ignoreTimezone(serviceItem?.start_date) ?? null), 'MM/dd/yyyy')}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h9" color="text.primary">
                            End Date:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {format(new Date(ignoreTimezone(serviceItem?.end_date) ?? null), 'MM/dd/yyyy')}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Total Units :
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {thousantSeparator(serviceItem?.total_units)}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Used:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {'$' +
                                thousantSeparator(serviceItem?.used_units) +
                                ' (' +
                                (serviceItem?.total_units == 0 ? 0 : Math.round((100 * serviceItem?.used_units) / serviceItem?.total_units)) +
                                '%)'}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h9" color="text.primary">
                            Remaining:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {'$' +
                                thousantSeparator(serviceItem?.remaining_units) +
                                ' (' +
                                (serviceItem?.total_units == 0 ? 0 : Math.round((100 * serviceItem?.remaining_units) / serviceItem?.total_units)) +
                                '%)'}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Total Service Rate:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            ${thousantSeparator(serviceItem?.total_units * serviceItem?.rate_per_unit)}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Rate per Unit:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            ${thousantSeparator(serviceItem?.rate_per_unit)}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            Payor:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {serviceAgreement?.payer_name}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h9" color="text.primary">
                            MA/MCO Number
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {serviceAgreement?.ma_mco_number}
                        </Typography>
                    </div>
                </>
            ) : (
                <>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Unit Type:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {serviceItem?.billing_unit?.replace('_', ' ')}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Start Date:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {format(new Date(ignoreTimezone(serviceItem?.start_date) ?? null), 'MM/dd/yyyy')}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h8" color="text.primary">
                            End Date:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {format(new Date(ignoreTimezone(serviceItem?.end_date) ?? null), 'MM/dd/yyyy')}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Total Units :
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {thousantSeparator(serviceItem?.total_units)}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h8" color="text.primary">
                            Used:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {'$' +
                                thousantSeparator(serviceItem?.used_units) +
                                ' (' +
                                (serviceItem?.total_units == 0 ? 0 : Math.round((100 * serviceItem?.used_units) / serviceItem?.total_units)) +
                                '%)'}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h8" color="text.primary">
                            Remaining:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {'$' +
                                thousantSeparator(serviceItem?.remaining_units) +
                                ' (' +
                                (serviceItem?.total_units == 0 ? 0 : Math.round((100 * serviceItem?.remaining_units) / serviceItem?.total_units)) +
                                '%)'}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Total Service Rate:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            ${thousantSeparator(serviceItem?.total_units * serviceItem?.rate_per_unit)}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h8" color="text.primary">
                            Rate per Unit:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            ${thousantSeparator(serviceItem?.rate_per_unit)}
                        </Typography>
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Payor:
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {serviceAgreement?.payer_name}
                        </Typography>
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h8" color="text.primary">
                            MA/MCO Number
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                            {serviceAgreement?.ma_mco_number}
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <Typography variant="h8" color="text.primary">
                Service Activities:
            </Typography>
            <br />
            {serviceItem?.activities?.map((item, index) => (
                <div key={index}>
                    <br />
                    <div className="activity-card">
                        <Typography variant="h7" color="primary">
                            {item?.activity_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {item?.instructions}
                        </Typography>
                    </div>
                </div>
            ))}
        </>
    )
}
